// Port of lib/scoring.py — the deterministic candidate-time ranking engine.
//
// ADAPTATIONS versus Python (all noted in the port report):
//  - Python's float(None) raises TypeError (NOT caught by the `except (KeyError, ValueError)`
//    guards around event conversion); the TS port mirrors this with the builtin TypeError,
//    which the catch sites rethrow.

import { calculate_chart } from "../astro/chart";
import { birth_offset_at } from "./timezones";
import { get_config } from "../astro/config";
import type { LifeEvent } from "./event_model";
import {
  createLifeEvent,
  dasha_timing_defaults,
  score_birth_time,
  targets_from_category,
  weighted_mean,
} from "./event_model";
import { append_verbose_log, candidate_list_from_times, store_candidate_times } from "./json_store";
import { age_years_from_birth, combined_trait_answers, score_traits_for_candidate } from "./traits";
import { score_life_periods_for_candidate } from "./life_periods";
import { _event_datetime_range, event_precision_weight_multiplier, optional_float } from "./scoring_common";
import { score_kp_for_candidate, FINE_BOOST_MAX_SPAN_SECONDS } from "./kp_scoring";
import { score_special_points_for_candidate } from "./special_points";
import { TIME_PRIOR_TIERS, parse_time_seconds, time_prior_factor } from "./time_prior";
import { combine_evidence, appearance_conflict_note } from "./evidence_weights";
import type { PlainDate, PlainDateTime } from "./datetime";
import {
  addDays,
  addSeconds,
  compareDates,
  diffDays,
  diffSeconds,
  formatISODate,
  fromOrdinal,
  parseISODate,
  plainDateTime,
  secondsOfDay,
  toOrdinal,
} from "./datetime";
import { _py_float, _truthy, pythonRound } from "./pyutil";
import type { RawChartData } from "../astro/models";

/** A candidate clock second as (hour, minute, second), mirroring Python's tuple[int, int, int]. */
export type CandidateTuple = [number, number, number];

// Deterministic ranking only: the KP signed-significator engine (Ascendant/cuspal
// sub-lords + event-date dasha significators) blended with the chart-derived trait
// engine (rising sign from Vol_06). No AI signal. Bumping this string regenerates any
// stale cached candidates.
export const DETERMINISTIC_GENERATION_METHOD = "astro_twostage_eventfit_risingsign_nocert_v23";

// ── Evidence lanes ────────────────────────────────────────────────────────────────────
// Evidence sits in two resolution lanes:
//   EventFit (ET): dated events (KP) — the ONLY second-resolution signal; ranks the exact
//       time. Dated events are ground truth, so they stay authoritative for the time.
//   RisingSignFit (RS): traits + life-periods — fallible self-reflection that is
//       constant across a rising sign (~2h). It SELECTS the rising sign, never the second,
//       so it can't add second-level noise.
// These are combined by the WEIGHTED EVIDENCE MODEL (`lib/evidence_weights.combine_evidence`):
// dated events 50% (primary), personality 20%, appearance 15% (bounded — can never veto a
// chart), family 10%, archetype 5%. The earlier adaptive `RS_LAMBDA`/`adaptive_rs_lambda`
// scheme (which let appearance grow when events were few — the exact over-reliance the Depp
// case exposed) was REMOVED in favour of these fixed, appearance-capped weights.
// Secondary Vedic checks weight (PLAN §6): the GRADED Pranapada confirmation (Vol_01 §9.3 /
// 01_sensitive_points_timing.md "Pranapad") adds at most SPECIAL_POINTS_LAMBDA × 50 points
// (= +3, full credit deep inside a credited house; proportionally less near a verdict-flip
// boundary — see graded_pranapada_score) — enough to break ties between otherwise-equal
// seconds (Pranapada moves a sign every 6 minutes), far too small to overrule the dated-event
// verdict. Never subtracts (score floor is neutral 50).
export const SPECIAL_POINTS_LAMBDA = 0.06;

// Relative trust among the rising-sign signals (used to blend them into RS, and to rank in
// the no-event case). Structured traits are the most reliable.
export const TRAIT_ENGINE_WEIGHT = 1.0;
export const LIFE_PERIOD_ENGINE_WEIGHT = 0.6;
export const DEFAULT_TOP_ASTRO_CANDIDATES = 10;
export const DEFAULT_AI_REVIEW_CANDIDATES = 10;
export const REFINEMENT_RESULTS_PER_PRIOR_TIME = 3;
export const POLISH_STEP_SECONDS = 1;
export const POLISH_RADIUS_SECONDS = 15;
export const FIRST_PASS_SPACING_FRACTION = 0.08;
// The D60 fine-divisional signal is only switched on once the search window is at or below this
// span (≈ "after the time is narrowed") — e.g. a locked single-sign window (~2h) or a drill-down.
// A full-day search stays well above it, so D60 never aliases on a wide scan. The SAME threshold
// gates the exact-second scan: at/below it every second is scanned; above it the first pass is
// coarse (a seed-finder), per the rectification method — coarse over a wide range, exact-second
// only after the window is narrowed (lock a rising sign or drill down).
export const FINE_DIVISIONAL_MAX_SPAN_SECONDS = 9000;
// Coarse first-pass step for a WIDE range. Nothing astrological resolves faster than this on a
// wide scan (rising sign ~2h, cuspal sub-lord a few min; D60's ~2min is deliberately off until
// the window is narrow), so a per-second sweep of the whole day is wasted work.
export const FIRST_PASS_COARSE_STEP_SECONDS = 60;
// Step for a NARROWED single-sign window (~2h). The score is piecewise-constant over cusp / D60
// segments (~minutes; measured ~40s blocks), so this resolves every real score change while a
// per-second sweep would recompute identical scores ~15-40x. The exact second is reached via
// drill-down (which sets an explicit step_seconds=1 over a tiny window).
export const SIGN_WINDOW_SCAN_STEP_SECONDS = 15;

// ---------------------------------------------------------------------------
// Python-semantics helpers (module-private)
// ---------------------------------------------------------------------------

// _truthy / _py_float are the shared Python-semantics helpers from ./pyutil.

/** payload.get(key, default) passed through float(). */
function _py_float_get(payload: Record<string, any>, key: string, default_: number): number {
  return _py_float(key in payload ? payload[key] : default_);
}

/** Python `not in (None, "", [], {})`. */
function _not_empty(value: any): boolean {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return false;
  return true;
}

// ---------------------------------------------------------------------------

export function birth_data_for_scoring(
  user_profile: Record<string, any>,
  fallback_time: CandidateTuple = [0, 0, 0],
): Record<string, Record<string, any>> {
  const birth = user_profile["birth"];
  const birth_date = parseISODate(birth["date"]);
  return {
    [user_profile["slug"]]: {
      year: birth_date.year,
      month: birth_date.month,
      date: birth_date.day,
      hour: fallback_time[0],
      minutes: fallback_time[1],
      seconds: fallback_time[2],
      lat: _py_float(birth["lat"]),
      lon: _py_float(birth["lon"]),
      timezone_offset: _py_float(birth["timezone_offset"]),
      // Carried through so birth-side offset resolution can honour a user-confirmed fixed offset
      // (pre-1970 births where the tzdb DST projection is unreliable). See `birth_offset_at`.
      timezone_offset_fixed: Boolean(birth["timezone_offset_fixed"]),
    },
  };
}

export function second_candidate_tuples(search: Record<string, any>): CandidateTuple[] {
  const start_seconds = candidate_seconds(time_label_to_tuple(search["start"]));
  let end_seconds = candidate_seconds(time_label_to_tuple(search["end"]));
  if (end_seconds < start_seconds) {
    end_seconds += 86400;
  }
  const step_seconds = Math.max(1, Math.trunc(Number(_truthy(search["step_seconds"]) ? search["step_seconds"] : 1)));
  const candidates: CandidateTuple[] = [];
  for (let value = start_seconds; value <= end_seconds; value += step_seconds) {
    const seconds = value % 86400;
    candidates.push([Math.floor(seconds / 3600), Math.floor((seconds % 3600) / 60), seconds % 60]);
  }
  return candidates;
}

export function search_span_seconds(search: Record<string, any>): number {
  // Date-aware when the window carries calendar bounds (start_date/end_date). This is a strict
  // generalisation: a same-day window (start_date == end_date) and the single-overnight case
  // (end_date == start_date + 1, end < start) both reduce to EXACTLY the old time-of-day math,
  // so single-day/overnight behaviour — and every test pinning it — is unchanged. Only a true
  // multi-day window (end_date ≥ start_date + 1 with a span > 24h, e.g. induced-delivery search)
  // now reports its real width instead of being capped at one day.
  const start_seconds = candidate_seconds(time_label_to_tuple(search["start"]));
  const end_seconds_raw = candidate_seconds(time_label_to_tuple(search["end"]));
  const sd = search_start_date(search);
  const ed = search_end_date(search);
  if (sd && ed) {
    const total = diffDays(ed, sd) * 86400 + (end_seconds_raw - start_seconds);
    // Defensive: an inconsistent date pair (e.g. an overnight window whose end date was lost, so
    // end_date == start_date while end < start) would otherwise clamp to a 1-second span. Fall
    // through to the circular time-of-day math instead of reporting a collapsed window.
    if (total > 0) {
      return total;
    }
  }
  let end_seconds = end_seconds_raw;
  if (end_seconds < start_seconds) {
    end_seconds += 86400;
  }
  return Math.max(1, end_seconds - start_seconds);
}

// ── Multi-day search support ───────────────────────────────────────────────────────────────
// The candidate model is time-of-day only (`CandidateTuple = [h,m,s]`, keyed by "HH:MM:SS") and
// resolves a candidate's calendar day via `candidate_birth_date`, which only distinguishes the
// two boundary dates (start_date / end_date) of a single overnight crossing. A window spanning
// MORE than one midnight (induced-delivery "search the next few days") needs each candidate to
// carry its actual date. Rather than rewrite the time-of-day engine, multi-day candidates carry
// an EXPLICIT date threaded alongside the tuple, and are keyed by a DATE-QUALIFIED label
// ("YYYY-MM-DDTHH:MM:SS") so two candidates at the same clock time on different days never
// collide. Single-day/overnight windows keep the plain "HH:MM:SS" key and behave identically.

export function search_start_date(search: Record<string, any> | null): PlainDate | null {
  if (!_truthy(search)) return null;
  const raw = search!["start_date"];
  if (!_truthy(raw)) return null;
  try {
    return parseISODate(String(raw));
  } catch {
    return null;
  }
}

export function search_end_date(search: Record<string, any> | null): PlainDate | null {
  if (!_truthy(search)) return null;
  const raw = search!["end_date"];
  if (!_truthy(raw)) return null;
  try {
    return parseISODate(String(raw));
  } catch {
    return null;
  }
}

/** True when the window spans more than 24h (a genuine multi-day search, not a single overnight). */
export function is_multiday_search(search: Record<string, any> | null): boolean {
  if (!_truthy(search)) return false;
  const sd = search_start_date(search);
  const ed = search_end_date(search);
  if (!sd || !ed) return false;
  try {
    return search_span_seconds(search!) > 86400;
  } catch {
    return false;
  }
}

const _DATED_KEY_RE = /^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}:\d{1,2}:\d{1,2})$/;

/** A candidate key with an explicit calendar day. Multi-day uses date-qualified labels. */
export function is_dated_key(key: string): boolean {
  return _DATED_KEY_RE.test(String(key).trim());
}

/** Build a candidate key: date-qualified ("YYYY-MM-DDTHH:MM:SS") when a date is given, else plain. */
export function make_candidate_key(candidate: CandidateTuple, date: PlainDate | null): string {
  const t = time_label(candidate);
  return date ? `${formatISODate(date)}T${t}` : t;
}

/** Parse a candidate key (plain or date-qualified) into its date (null if undated) and tuple. */
export function parse_candidate_key(key: string): { date: PlainDate | null; tuple: CandidateTuple } {
  const m = _DATED_KEY_RE.exec(String(key).trim());
  if (m) {
    return { date: parseISODate(m[1]), tuple: time_label_to_tuple(m[2]) };
  }
  return { date: null, tuple: time_label_to_tuple(key) };
}

/**
 * Absolute, monotonic seconds for a candidate KEY, used for spacing/bucketing/dedup so the
 * selection layer works for both single-day and multi-day windows. A date-qualified key returns
 * proleptic epoch seconds (toOrdinal·86400 + seconds-of-day); a plain "HH:MM:SS" key returns its
 * seconds-of-day (0..86399) — IDENTICAL to the old `candidate_seconds(time_label_to_tuple(key))`,
 * so single-day selection is byte-for-byte unchanged.
 */
export function candidate_key_seconds(key: string): number {
  const m = _DATED_KEY_RE.exec(String(key).trim());
  if (m) {
    return toOrdinal(parseISODate(m[1])) * 86400 + candidate_seconds(time_label_to_tuple(m[2]));
  }
  return candidate_seconds(time_label_to_tuple(key));
}

/**
 * Gap between two candidate keys for the spacing rule. Date-qualified (multi-day) keys use a
 * LINEAR gap on absolute seconds; plain keys keep the 24h CIRCULAR delta (so a window straddling
 * midnight still treats 23:59 and 00:01 as two minutes apart).
 */
export function candidate_key_gap(a_key: string, b_key: string): number {
  const dated = is_dated_key(a_key) || is_dated_key(b_key);
  const a = candidate_key_seconds(a_key);
  const b = candidate_key_seconds(b_key);
  return dated ? Math.abs(a - b) : circular_seconds_delta(a, b);
}

const _HMS_RE = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/;

/** Python datetime.strptime(value, "%H:%M:%S").time() — seconds REQUIRED, raises on mismatch. */
export function time_label_to_tuple(value: string): CandidateTuple {
  const m = _HMS_RE.exec(String(value));
  if (!m) {
    throw new Error(`time data '${value}' does not match format '%H:%M:%S'`);
  }
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  const second = Number(m[3]);
  if (hour > 23 || minute > 59 || second > 59) {
    throw new Error(`time data '${value}' does not match format '%H:%M:%S'`);
  }
  return [hour, minute, second];
}

export function candidate_seconds(candidate: CandidateTuple): number {
  return candidate[0] * 3600 + candidate[1] * 60 + candidate[2];
}

export function circular_seconds_delta(first: number, second: number): number {
  const delta = Math.abs(first - second);
  return Math.min(delta, 86400 - delta);
}

export function adaptive_min_gap_seconds(user_profile: Record<string, any>): number {
  const loop_count = Math.trunc(Number(user_profile["loop_count"] ?? 0));
  if (loop_count <= 0) {
    return Math.max(1, Math.trunc(pythonRound(search_span_seconds(user_profile["search"]) * FIRST_PASS_SPACING_FRACTION)));
  }
  if (loop_count === 1) {
    return 60;
  }
  return 1;
}

export function refinement_window_radius_seconds(user_profile: Record<string, any>): number {
  const span = search_span_seconds(user_profile["search"]);
  const loop_count = Math.trunc(Number(user_profile["loop_count"] ?? 0));
  if (loop_count <= 0) {
    return span;
  }
  if (loop_count === 1) {
    return Math.max(1800, Math.trunc(pythonRound((span * FIRST_PASS_SPACING_FRACTION) / 2)));
  }
  if (loop_count === 2) {
    return Math.max(300, Math.trunc(pythonRound(span * 0.01)));
  }
  return Math.max(60, Math.trunc(pythonRound(span * 0.0025)));
}

export function refinement_window_step_seconds(user_profile: Record<string, any>): number {
  // Loop 1 refinement windows are still wide (up to ~hours around each winner) and the
  // score there is piecewise-constant over ~minutes (D60 stays off on a wide span), so a
  // 15s sweep catches every real change without recomputing flat plateaus per second.
  // From loop 2 the windows are tight; sweep every second for the exact moment.
  return Math.trunc(Number(user_profile["loop_count"] ?? 0)) <= 1 ? SIGN_WINDOW_SCAN_STEP_SECONDS : 1;
}

export function seconds_to_candidate(value: number): CandidateTuple {
  value = ((value % 86400) + 86400) % 86400;
  return [Math.floor(value / 3600), Math.floor((value % 3600) / 60), value % 60];
}

export function candidate_in_search_range(candidate: CandidateTuple, search: Record<string, any>): boolean {
  const start_seconds = candidate_seconds(time_label_to_tuple(search["start"]));
  const end_seconds = candidate_seconds(time_label_to_tuple(search["end"]));
  const current_seconds = candidate_seconds(candidate);
  if (end_seconds < start_seconds) {
    return current_seconds >= start_seconds || current_seconds <= end_seconds;
  }
  return start_seconds <= current_seconds && current_seconds <= end_seconds;
}

export function stepped_refinement_tuples(
  center: CandidateTuple,
  radius_seconds: number,
  step_seconds: number,
  search: Record<string, any>,
): CandidateTuple[] {
  const center_seconds = candidate_seconds(center);
  const candidates: CandidateTuple[] = [];
  for (let offset = -radius_seconds; offset <= radius_seconds; offset += step_seconds) {
    const candidate = seconds_to_candidate(center_seconds + offset);
    if (candidate_in_search_range(candidate, search)) {
      candidates.push(candidate);
    }
  }
  return candidates;
}

export function multi_window_candidate_tuples(
  user_profile: Record<string, any>,
  candidate_times: Array<Record<string, any>>,
): CandidateTuple[] {
  const candidates = new Set<number>();
  const radius_seconds = refinement_window_radius_seconds(user_profile);
  const step_seconds = refinement_window_step_seconds(user_profile);
  const search = user_profile["search"];
  for (const item of candidate_times) {
    if (_truthy(item["rejected"])) {
      continue;
    }
    const center = time_label_to_tuple(item["time"]);
    for (const candidate of stepped_refinement_tuples(center, radius_seconds, step_seconds, search)) {
      candidates.add(candidate_seconds(candidate));
    }
  }
  return [...candidates].sort((a, b) => a - b).map(seconds_to_candidate);
}

export function time_label(candidate: CandidateTuple): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(candidate[0])}:${pad(candidate[1])}:${pad(candidate[2])}`;
}

/**
 * Resolve which calendar date a candidate second belongs to. For an overnight
 * window (end_date after start_date) any candidate earlier than the start time has
 * crossed midnight and belongs to the end date; otherwise it is the birth date.
 */
export function candidate_birth_date(
  birth_info: Record<string, any>,
  candidate: CandidateTuple,
  search: Record<string, any> | null,
): PlainDate {
  const base: PlainDate = { year: birth_info["year"], month: birth_info["month"], day: birth_info["date"] };
  if (!_truthy(search)) {
    return base;
  }
  const start_date_raw = search!["start_date"];
  const end_date_raw = search!["end_date"];
  if (!_truthy(start_date_raw) || !_truthy(end_date_raw) || start_date_raw === end_date_raw) {
    return base;
  }
  let start_date: PlainDate;
  let end_date: PlainDate;
  let start_seconds: number;
  try {
    start_date = parseISODate(start_date_raw);
    end_date = parseISODate(end_date_raw);
    if (!("start" in search!)) {
      throw new Error("KeyError: 'start'");
    }
    start_seconds = candidate_seconds(time_label_to_tuple(search!["start"]));
  } catch {
    return base;
  }
  if (compareDates(end_date, start_date) <= 0) {
    return base;
  }
  return candidate_seconds(candidate) < start_seconds ? end_date : start_date;
}

/**
 * The UTC offset actually in force at the birth place at this CANDIDATE moment.
 * Normally identical to the profile's stored offset, but on a DST-transition birth date
 * the offset changes partway through the search window — the one day a fixed offset is
 * wrong is a day defined entirely by clock politics, in the product whose whole job is
 * the clock time. Falls back to the stored offset when the zone cannot be resolved.
 */
export function candidate_timezone_offset(
  candidate: CandidateTuple,
  birth_info: Record<string, any>,
  timezone_offset: number,
  search: Record<string, any> | null = null,
  candidate_date: PlainDate | null = null,
): number {
  // A multi-day scan threads the candidate's actual day explicitly; otherwise resolve it from the
  // window (single-day / one-overnight) via candidate_birth_date.
  const day = candidate_date ?? candidate_birth_date(birth_info, candidate, search);
  const probe_dt = plainDateTime(day.year, day.month, day.day, candidate[0], candidate[1], candidate[2]);
  return birth_offset_at(birth_info, probe_dt, timezone_offset);
}

/**
 * Build the birth datetime and natal chart for a candidate second. Computed once
 * per candidate and shared by every scorer so the per-second scan calculates each
 * chart exactly once. `search` carries the window dates so overnight candidates land
 * on the correct calendar day. The chart uses the offset in force AT the candidate
 * moment (candidate_timezone_offset), not blindly the profile's stored offset.
 */
export function natal_chart_for_candidate(
  candidate: CandidateTuple,
  birth_info: Record<string, any>,
  timezone_offset: number,
  search: Record<string, any> | null = null,
  candidate_date: PlainDate | null = null,
): [PlainDateTime, RawChartData] {
  const day = candidate_date ?? candidate_birth_date(birth_info, candidate, search);
  const birth_dt = plainDateTime(day.year, day.month, day.day, candidate[0], candidate[1], candidate[2]);
  const tz_offset = birth_offset_at(birth_info, birth_dt, timezone_offset);
  const natal_chart = calculate_chart(birth_dt, _py_float(birth_info["lat"]), _py_float(birth_info["lon"]), tz_offset, get_config());
  return [birth_dt, natal_chart];
}

export function event_payload_to_life_event(payload: Record<string, any>): LifeEvent {
  if (!_truthy(payload["start_date"])) {
    throw new Error("Event start_date is required for deterministic scoring.");
  }
  const [start_dt, end_dt, has_exact_time] = _event_datetime_range(payload);
  const event_dt = has_exact_time ? start_dt : addSeconds(start_dt, diffSeconds(end_dt, start_dt) / 2);
  const category = payload["category"];
  if (!("category" in payload)) {
    // Python payload["category"] raises KeyError, caught by the conversion guards.
    throw new Error("KeyError: 'category'");
  }
  const polarity_raw = String(payload["polarity"] ?? "positive").toLowerCase();
  const polarity = polarity_raw === "negative" ? -1 : polarity_raw === "neutral" ? 0 : 1;
  const severity = Math.max(0.0, Math.min(5.0, _py_float_get(payload, "severity", 3.0)));
  const confidence = Math.max(0.0, Math.min(1.0, _py_float_get(payload, "confidence", 1.0)));
  const weight = Math.max(0.0, _py_float_get(payload, "weight", 1.0));
  const [ranges, raw_values, metric_weights] = targets_from_category(category, polarity, severity);
  const [dasha_weight, dasha_window] = dasha_timing_defaults(category);
  const date_span_days = diffSeconds(end_dt, start_dt) / 86400;
  const date_accuracy_days = Math.max(
    date_span_days / 2,
    _py_float(_truthy(payload["date_accuracy_days"]) ? payload["date_accuracy_days"] : 0.0),
  );
  const precision_multiplier = event_precision_weight_multiplier(date_accuracy_days, has_exact_time);
  const start_date = { year: start_dt.year, month: start_dt.month, day: start_dt.day };
  return createLifeEvent({
    date: event_dt,
    label: String(_truthy(payload["label"]) ? payload["label"] : `Event on ${formatISODate(start_date)}`).slice(0, 180),
    expected_ranges: ranges,
    raw_values: raw_values,
    metric_weights: metric_weights,
    category: category,
    polarity: polarity,
    severity: severity,
    confidence: confidence,
    weight: weight * precision_multiplier,
    date_accuracy_days: date_accuracy_days,
    has_exact_time: has_exact_time,
    event_lat: optional_float(payload["event_lat"]),
    event_lon: optional_float(payload["event_lon"]),
    dasha_timing_weight: dasha_weight,
    dasha_timing_window_days: dasha_window,
    start_date: start_dt,
    end_date: end_dt,
  });
}

export function score_directional_metric(predicted: number, expected_direction: string): number {
  const direction = expected_direction.trim().toLowerCase();
  predicted = Math.max(-5.0, Math.min(5.0, Number(predicted)));
  if (direction === "negative") {
    return Math.max(0.0, Math.min(100.0, (5.0 - predicted) * 10.0));
  }
  if (direction === "neutral") {
    return Math.max(0.0, 100.0 - Math.abs(predicted) * 20.0);
  }
  return Math.max(0.0, Math.min(100.0, (predicted + 5.0) * 10.0));
}

export function selected_candidate_tuples(user_profile: Record<string, any>): CandidateTuple[] {
  const active_candidates = candidate_list_from_times(user_profile);
  if (Math.trunc(Number(user_profile["loop_count"] ?? 0)) > 0 && active_candidates.length) {
    return multi_window_candidate_tuples(user_profile, active_candidates);
  }
  const search = user_profile["search"];
  // Adaptive first-pass resolution, matching the rectification method (coarse → narrow → exact):
  //  - An explicit step_seconds (e.g. a drill-down) is always honoured.
  //  - A NARROW window (a locked single rising sign, ~2h, or tighter) is swept at EVERY SECOND —
  //    this is where the exact birth time is resolved (and where D60's ~2min signal is engaged).
  //  - A WIDE window is swept COARSELY as a seed-finder; the exact second is found only after the
  //    rising sign / window is narrowed. Scanning a whole day per-second changes nothing
  //    astrological that a ~minute grid misses, so it is pure wasted computation.
  if (_truthy(search["step_seconds"])) {
    return second_candidate_tuples(search); // explicit (e.g. drill-down at 1s) is honoured
  }
  if (search_span_seconds(search) <= FINE_DIVISIONAL_MAX_SPAN_SECONDS) {
    // Narrowed single sign (~2h): step finely enough to catch every real score change without
    // recomputing flat plateaus per-second. Drill down on a candidate for the exact second.
    return second_candidate_tuples({ ...search, step_seconds: SIGN_WINDOW_SCAN_STEP_SECONDS });
  }
  return second_candidate_tuples({ ...search, step_seconds: FIRST_PASS_COARSE_STEP_SECONDS });
}

/** A candidate to score, carrying its explicit calendar day (null = resolve from the window). */
export interface ScanCandidate {
  tuple: CandidateTuple;
  date: PlainDate | null;
}

/**
 * Coarse dated sweep of a MULTI-DAY window. The same rising signs recur each day, so a genuine
 * multi-day search (induced-delivery "the birth may have been a day or two earlier") is a seed
 * finder across the whole span at the coarse grid; the user then COMMITS a day+rising-sign, which
 * narrows the window to a single ≤2h slice where the normal fine machinery (and the loop-count
 * refinement) takes over. We therefore deliberately do NOT run the per-winner refinement loop on
 * a multi-day window — every multi-day pass is a fresh coarse scan (run_internal_matching keeps
 * loop_count at 0 while multi-day).
 */
export function multiday_scan_candidates(search: Record<string, any>): ScanCandidate[] {
  const sd = search_start_date(search);
  if (!sd) return [];
  const start_sod = candidate_seconds(time_label_to_tuple(search["start"]));
  const span = search_span_seconds(search);
  const explicit = Math.trunc(Number(_truthy(search["step_seconds"]) ? search["step_seconds"] : 0));
  const step = Math.max(1, explicit > 0 ? explicit : FIRST_PASS_COARSE_STEP_SECONDS);
  const out: ScanCandidate[] = [];
  for (let off = 0; off <= span; off += step) {
    const abs = start_sod + off;
    const date = addDays(sd, Math.floor(abs / 86400));
    const tod = ((abs % 86400) + 86400) % 86400;
    out.push({ tuple: seconds_to_candidate(tod), date });
  }
  return out;
}

/**
 * The candidates to score this pass, each tagged with its calendar day. Single-day / overnight
 * windows reuse `selected_candidate_tuples` verbatim (date left null → resolved downstream exactly
 * as before), so their behaviour is unchanged; a multi-day window returns the dated coarse sweep.
 */
export function selected_scan_candidates(user_profile: Record<string, any>): ScanCandidate[] {
  const search = user_profile["search"];
  if (is_multiday_search(search)) {
    return multiday_scan_candidates(search);
  }
  return selected_candidate_tuples(user_profile).map((tuple) => ({ tuple, date: null }));
}

/** Stable descending sort by deterministic_score (Python sorted(..., reverse=True)). */
function _sorted_by_score_desc(scored: Array<Record<string, any>>): Array<Record<string, any>> {
  return [...scored].sort((a, b) => Number(b["deterministic_score"]) - Number(a["deterministic_score"]));
}

export function select_spaced_candidates_seconds(
  scored: Array<Record<string, any>>,
  limit: number,
  min_gap_seconds: number,
): Array<Record<string, any>> {
  const selected: Array<Record<string, any>> = [];
  const min_gap = Math.max(1, min_gap_seconds);
  for (const candidate of _sorted_by_score_desc(scored)) {
    if (
      selected.every(
        (item) => candidate_key_gap(candidate["time"], item["time"]) >= min_gap,
      )
    ) {
      selected.push(candidate);
    }
    if (selected.length >= limit) {
      break;
    }
  }
  return selected;
}

export function select_top_scored_candidates(
  scored: Array<Record<string, any>>,
  limit: number,
): Array<Record<string, any>> {
  return _sorted_by_score_desc(scored).slice(0, limit);
}

export function bucket_start_seconds(time_value: string, bucket_minutes = 5): number {
  const bucket_seconds = Math.max(1, bucket_minutes) * 60;
  return Math.floor(candidate_key_seconds(time_value) / bucket_seconds) * bucket_seconds;
}

export function select_bucket_winners(
  scored: Array<Record<string, any>>,
  limit: number,
  bucket_minutes = 5,
): Array<Record<string, any>> {
  const winners = new Map<number, Record<string, any>>();
  for (const candidate of scored) {
    const bucket = bucket_start_seconds(candidate["time"], bucket_minutes);
    const current = winners.get(bucket);
    if (current === undefined || Number(candidate["deterministic_score"]) > Number(current["deterministic_score"])) {
      winners.set(bucket, candidate);
    }
  }

  const selected: Array<Record<string, any>> = [];
  const min_gap = Math.max(1, bucket_minutes) * 60;
  for (const candidate of _sorted_by_score_desc([...winners.values()])) {
    if (
      selected.every(
        (item) => candidate_key_gap(candidate["time"], item["time"]) >= min_gap,
      )
    ) {
      selected.push(candidate);
    }
    if (selected.length >= limit) {
      break;
    }
  }
  return selected;
}

export function local_peak_records(scored: Array<Record<string, any>>): Array<Record<string, any>> {
  const ordered = [...scored].sort(
    (a, b) => candidate_key_seconds(a["time"]) - candidate_key_seconds(b["time"]),
  );
  if (!ordered.length) {
    return [];
  }
  if (ordered.length === 1) {
    return ordered;
  }

  const peaks: Array<Record<string, any>> = [];
  for (let index = 0; index < ordered.length; index++) {
    const item = ordered[index];
    const current = Number(item["deterministic_score"]);
    const previous_score = index > 0 ? Number(ordered[index - 1]["deterministic_score"]) : null;
    const next_score = index < ordered.length - 1 ? Number(ordered[index + 1]["deterministic_score"]) : null;

    let is_peak: boolean;
    if (previous_score === null) {
      is_peak = next_score !== null && current > next_score;
    } else if (next_score === null) {
      is_peak = current > previous_score;
    } else {
      is_peak =
        current >= previous_score && current >= next_score && (current > previous_score || current > next_score);
    }
    if (is_peak) {
      peaks.push(item);
    }
  }

  // Python max() returns the FIRST maximal element.
  let best = ordered[0];
  for (const value of ordered) {
    if (Number(value["deterministic_score"]) > Number(best["deterministic_score"])) {
      best = value;
    }
  }
  if (!peaks.some((item) => item["time"] === best["time"])) {
    peaks.push(best);
  }
  return _sorted_by_score_desc(peaks);
}

export function select_top_per_previous_center(
  scored: Array<Record<string, any>>,
  previous_candidates: Array<Record<string, any>>,
  per_center: number = REFINEMENT_RESULTS_PER_PRIOR_TIME,
  min_gap_seconds = 1,
): Array<Record<string, any>> {
  if (!previous_candidates.length) {
    return select_spaced_candidates_seconds(scored, scored.length, min_gap_seconds);
  }

  const previous_times = previous_candidates.filter((item) => _truthy(item["time"])).map((item) => item["time"]);
  const grouped = new Map<string, Array<Record<string, any>>>();
  for (const time_value of previous_times) {
    if (!grouped.has(time_value)) grouped.set(time_value, []);
  }
  for (const candidate of scored) {
    // Python min(..., key=...) — FIRST minimal previous time wins ties.
    let nearest = previous_times[0];
    let nearest_delta = candidate_key_gap(candidate["time"], nearest);
    for (let i = 1; i < previous_times.length; i++) {
      const delta = candidate_key_gap(candidate["time"], previous_times[i]);
      if (delta < nearest_delta) {
        nearest = previous_times[i];
        nearest_delta = delta;
      }
    }
    grouped.get(nearest)!.push(candidate);
  }

  const selected: Array<Record<string, any>> = [];
  for (const previous_time of previous_times) {
    selected.push(...select_spaced_candidates_seconds(grouped.get(previous_time) ?? [], per_center, min_gap_seconds));
  }
  return _sorted_by_score_desc(selected);
}

/**
 * "Explained events" at one candidate moment — the metric that REPLACED the per-sign Accuracy
 * (holdout) signal (2026-07-20). Measured on the 4 AA reference charts, per-sign leave-one-out
 * holdout ranked the TRUE sign 7.75/12 on average (worse than chance — its per-fold
 * re-rectification rewards signs whose loose fits let every fold chase a flattering time) while
 * this fixed-time reading blends with Events at avg rank 3.00 (improve-or-hold on all four).
 * It is also the classical acceptance rule made visible: a candidate should EXPLAIN the events
 * (score them above neutral) and not contradict major facts. Returns the weighted percentage of
 * events scoring > 50 at this record's moment plus the plain counts for display ("6/7").
 * Attached to every candidate record, so Level 0 (per sign), the Level-1 drill-down, and the
 * Compare page all read the SAME definition.
 */
export function explained_events_at(record: Record<string, any>): {
  fraction: number; explained_count: number; events_total: number;
} | null {
  const pairs: Array<[number, number]> = [];
  for (const detail of record["kp_details"] ?? []) {
    if (detail["score"] === null || detail["score"] === undefined) continue; // Number(null) is 0
    const score = Number(detail["score"]);
    const weight = Number(detail["effective_weight"]);
    if (!Number.isFinite(score) || !Number.isFinite(weight) || weight <= 0) continue;
    pairs.push([score, weight]);
  }
  if (!pairs.length) return null;
  const total_w = pairs.reduce((s, [, w]) => s + w, 0);
  const explained_w = pairs.filter(([v]) => v > 50).reduce((s, [, w]) => s + w, 0);
  return {
    fraction: pythonRound((explained_w / total_w) * 100.0, 1),
    explained_count: pairs.filter(([v]) => v > 50).length,
    events_total: pairs.length,
  };
}

export function score_candidate_record(
  people_key: string,
  candidate: CandidateTuple,
  birth_data: Record<string, Record<string, any>>,
  life_events: LifeEvent[],
  raw_events: Array<Record<string, any>>,
  timezone_offset: number,
  range_metadata: Record<string, any>,
  search_pass: string,
  coarse_seed_time: string | null = null,
  search: Record<string, any> | null = null,
  trait_answers: Record<string, any> | null = null,
  life_period_answers: Record<string, any> | null = null,
  appearance_likelihood: Record<string, number> | null = null,
  candidate_date: PlainDate | null = null,
): Record<string, any> {
  void range_metadata; // kept for signature fidelity with Python (unused there too)
  const birth_info = birth_data[people_key];
  // The offset in force AT this candidate moment (differs from the stored profile offset
  // only on a DST-transition birth date); every birth-side conversion below shares it so
  // the natal chart and the dasha seed stay consistent. `candidate_date`, when present (a
  // multi-day scan), pins the candidate's calendar day; otherwise it is resolved from the window.
  const candidate_tz = candidate_timezone_offset(candidate, birth_info, timezone_offset, search, candidate_date);
  // One chart per candidate, shared by every event-based scorer.
  const [birth_dt, natal_chart] = natal_chart_for_candidate(candidate, birth_info, timezone_offset, search, candidate_date);
  // Professional KP + dasha-significator engine: always on, chart-derived, and the
  // sharpest birth-time signal (Ascendant/cuspal sub-lords + sub-period chain).
  // D60 (the finest rectification chart) is engaged only once the window is already narrow —
  // i.e. during drill-down or a tight search — matching the classical rule to use it only after
  // the time is roughly fixed. A wide/full-day window leaves it off so it cannot add noise.
  let fine_divisional = false;
  // The second-sensitive component re-weighting engages only in the true drill-down regime
  // (span ≤ FINE_BOOST_MAX_SPAN_SECONDS ≈ 15 min) — re-weighting a whole ~2h sign window
  // overweights minute-noise components and measurably mis-ranks signs (Leo/Virgo holdout pin).
  let fine_boost = false;
  if (_truthy(search)) {
    try {
      const span = search_span_seconds(search!);
      fine_divisional = span <= FINE_DIVISIONAL_MAX_SPAN_SECONDS;
      fine_boost = span <= FINE_BOOST_MAX_SPAN_SECONDS;
    } catch {
      fine_divisional = false;
      fine_boost = false;
    }
  }
  const [kp_score, kp_details] = score_kp_for_candidate(
    natal_chart, birth_dt, birth_info, raw_events, candidate_tz, fine_divisional, fine_boost,
  );
  // The legacy category-metric score is fallback/diagnostic only and does not
  // rank candidates when a chart-based score exists (see PLAN.md Step 6).
  let score: number;
  let details: Array<Record<string, any>>;
  let event_scores: Array<Record<string, number>>;
  if (life_events.length && kp_score === null) {
    [score, details, event_scores] = score_birth_time(
      people_key,
      candidate,
      birth_data,
      life_events,
      timezone_offset,
      "window",
      6,
      9,
      "mean",
      "gaussian",
    );
  } else {
    [score, details, event_scores] = [0.0, [], []];
  }

  // ── EventFit (ET): the only second-resolution signal; events rank the exact time. ──
  const event_fit = kp_score;

  // ── Accuracy / leave-one-out stability (per-time): "test the events with vs. without each, and see
  // how much difference it makes." Where `event_fit` (above) is the WEIGHTED mean that RANKS the time,
  // this measures how ROBUST that score is: drop each dated event in turn, recompute the same weighted
  // mean from the rest, and take the WORST single-event swing. A time that ALL events agree on barely
  // moves whichever you drop → high accuracy; a time propped up by one event swings a lot → low. This
  // is the per-candidate, per-second projection of the window-level holdout (`holdout_validation`,
  // which actually re-searches the window without each event and is shown as the page verdict). Needs
  // ≥2 events to mean anything (a single event can't be held out), else null.
  // Zero-weight events (explicit confidence/weight 0) contribute nothing to the fit, so they are
  // excluded here too — otherwise dropping the LAST positive-weight event would leave _wmean's
  // zero-weight fallback averaging the zeroed events' scores, manufacturing a phantom swing.
  const _ev_pairs = (kp_details ?? [])
    .map((d) => [Number((d as Record<string, any>)["score"]), Number((d as Record<string, any>)["effective_weight"] ?? 1)] as [number, number])
    .filter(([s, w]) => !Number.isNaN(s) && !Number.isNaN(w) && w > 0);
  const _wmean = (pairs: Array<[number, number]>): number => {
    const wsum = pairs.reduce((a, [, w]) => a + w, 0);
    if (wsum <= 0) return pairs.reduce((a, [s]) => a + s, 0) / Math.max(1, pairs.length);
    return pairs.reduce((a, [s, w]) => a + s * w, 0) / wsum;
  };
  let accuracy_fit: number | null = null;
  if (_ev_pairs.length >= 2) {
    const full = _wmean(_ev_pairs);
    let max_swing = 0;
    for (let i = 0; i < _ev_pairs.length; i++) {
      const without_i = _wmean(_ev_pairs.filter((_, j) => j !== i));
      max_swing = Math.max(max_swing, Math.abs(full - without_i));
    }
    // Normalised by event count: the raw drop-one swing shrinks ~1/n, so with many events the
    // metric saturated to ~100 regardless of agreement. 100 − clamp(max_swing × n, 0, 100)
    // keeps it an inter-event AGREEMENT measure independent of how many events there are.
    accuracy_fit = Math.max(0, Math.min(100, 100 - Math.min(100, max_swing * _ev_pairs.length)));
  }

  // ── RisingSignFit (RS): coarse, per-rising-sign self-reflection (traits + life-periods).
  // Constant across the ~2h a sign rises, so it selects the sign, not the second.
  const pad = (n: number, w: number) => String(n).padStart(w, "0");
  const person_age = age_years_from_birth(
    `${pad(birth_info["year"], 4)}-${pad(birth_info["month"], 2)}-${pad(birth_info["date"], 2)}`,
  );
  const [trait_score, trait_details] = score_traits_for_candidate(natal_chart, trait_answers, person_age);
  // PHYSICAL-ONLY appearance (Ascendant body match, lagna-gated; Moon-disposition excluded). This —
  // not the Moon-inclusive `trait_score` — is the rising-sign appearance signal, so the Moon
  // (personality) can never carry lagna authority. Null when there are no physical answers (the
  // appearance bucket then simply drops out of the evidence combine). See `score_traits_for_candidate`.
  const appearance_physical =
    (trait_details as any)?.appearance_physical_score ?? null;
  const [life_period_score, life_period_details] = score_life_periods_for_candidate(
    natal_chart, birth_dt, life_period_answers, candidate_tz,
  );
  const rs_components: Array<[number, number]> = [];
  if (appearance_physical !== null) {
    rs_components.push([appearance_physical, TRAIT_ENGINE_WEIGHT]);
  }
  if (life_period_score !== null) {
    rs_components.push([life_period_score, LIFE_PERIOD_ENGINE_WEIGHT]);
  }
  const rising_sign_fit = rs_components.length ? weighted_mean(rs_components) : null;

  // ── Weighted evidence combine — dated events PRIMARY, appearance bounded & never decisive ──
  // Deterministic buckets only (events 50 · personality 20 · appearance 15 · family 10). A null
  // bucket drops out and the rest renormalise (`combine_evidence`), so:
  //   • events carry the largest share → the combined score tracks the event timing, and within a
  //     single rising sign only `events` varies by the second, so EVENTS STILL RANK THE EXACT TIME;
  //   • appearance is capped at its (renormalised) weight — a weak or even contradictory appearance
  //     can never zero out a strong-events chart (the Johnny-Depp / Cancer-that-doesn't-look-Cancer
  //     failure mode this guards against).
  //
  // RANKING NEVER consumes AI output. The `archetype` bucket (the AI `appearance_likelihood`) is
  // deliberately passed `null` so a nondeterministic model estimate cannot move the candidate
  // ranking (PLAN.md: AI never ranks). The AI likelihood is still SHOWN in the rising-sign
  // breakdown's `appearance_match` column — display-only, not ranking authority.
  const asc_sign_name = String(natal_chart.planets["ascendant"].sign_name);
  void appearance_likelihood; // intentionally NOT fed into ranking (display-only; see above)
  const evidence = combine_evidence({
    events: event_fit, // KP dasha/divisional — the only second-resolution signal (time authority)
    personality: life_period_score, // dasha-period life themes / life pattern
    appearance: appearance_physical, // PHYSICAL-only body match (Moon-disposition de-leaked; bounded)
    family: null, // durable family/body/residence facts removed — bucket always absent
    archetype: null, // AI gut-resemblance — EXCLUDED from ranking (nondeterministic; display-only)
  });
  let base_score: number;
  if (evidence.score !== null) {
    base_score = evidence.score;
  } else {
    base_score = score;
  }
  // Honest contradiction text: weak/contradictory appearance is explained, not allowed to veto.
  const appearance_conflict =
    appearance_conflict_note({
      appearance: appearance_physical ?? trait_score, // physical-only match drives the "weak looks" note
      events: event_fit,
      signName: asc_sign_name,
      signOnlyWeak: Boolean((trait_details as any)?.sign_only_weak),
    }) ?? (trait_details as any)?.appearance_note ?? null;
  // ── Secondary Vedic checks (PLAN §6 — corroborate, never decide) ──
  // Pranapada (Vol_01 §9.3 / 01_sensitive_points_timing.md "Pranapad"): one sign per 6
  // minutes, so it separates birth times WITHIN a rising sign. GRADED: full lift deep inside
  // a credited house, proportionally less near a verdict-flip boundary (the boundary is where
  // a few-minutes shift flips the verdict). TWO-SIDED: `trait_answers` carries the profile's
  // delivery_ease birth fact — an ordinary/easy (or unknown) birth expects the classically
  // auspicious houses (2/4/5/9/10/11 from the Ascendant); a KNOWN difficult/complicated
  // delivery expects the doc's inauspicious houses, so the credit flips to those candidates.
  // Additive-only either way (0..+3 via SPECIAL_POINTS_LAMBDA); uncredited candidates are
  // untouched — it must never veto a time.
  const special_points = score_special_points_for_candidate(
    natal_chart, birth_dt, birth_info, candidate_tz, trait_answers,
  );
  if (special_points.available && special_points.score !== null) {
    base_score += SPECIAL_POINTS_LAMBDA * (special_points.score - 50.0);
  }
  // RECORDED-TIME PRIOR (owner re-scope 2026-07-02 — see src/lib/time_prior.ts for the full
  // rationale): a `recorded`/`approximate` entered clock is EVIDENCE, so candidates far from it
  // are gently DISCOUNTED (bounded ×0.8/×0.9 floors — a decisive events lead still overturns a
  // mis-remembered clock; `range`/`unknown` certainties keep 1.0 everywhere, preserving the
  // original "a range never biases the ranking" rule and every blind benchmark byte-for-byte).
  // Keep the displayed score within 0-100: a strong event fit plus a strong rising-sign bias
  // can otherwise sum past 100, which is meaningless to the user.
  let prior_factor = time_prior_factor(
    birth_info["time_certainty"],
    parse_time_seconds(birth_info["entered_time"]),
    candidate_seconds(candidate),
  );
  // MULTI-DAY scan (date-qualified candidate): the recorded clock belongs to the ENTERED birth
  // date, so the same clock on the WRONG day must not collect the full prior. Recompute the
  // factor on the ABSOLUTE distance (day distance + clock delta) with the same tier curve —
  // bounded by the same floor, so still never a veto. Single-day windows are byte-unchanged.
  if (candidate_date !== null) {
    const day_tier = TIME_PRIOR_TIERS[String(birth_info["time_certainty"] ?? "").trim().toLowerCase()];
    const entered_seconds = parse_time_seconds(birth_info["entered_time"]);
    if (day_tier && entered_seconds !== null) {
      try {
        const birth_day = parseISODate(
          `${String(birth_info["year"]).padStart(4, "0")}-${String(birth_info["month"]).padStart(2, "0")}-${String(birth_info["date"]).padStart(2, "0")}`,
        );
        const delta_seconds = Math.abs(
          (toOrdinal(candidate_date) - toOrdinal(birth_day)) * 86400 +
            candidate_seconds(candidate) - entered_seconds,
        );
        const d = delta_seconds / 60.0 / day_tier.scale_minutes;
        prior_factor = day_tier.floor + (1.0 - day_tier.floor) * Math.exp(-0.5 * d * d);
      } catch {
        // Unparseable stored birth date — keep the clock-of-day prior.
      }
    }
  }
  // Accuracy-fit plateau TIE-BREAK: within a flat event-fit plateau prefer the second whose
  // events AGREE with each other (bounded — at most 2 points, far below any real evidence step).
  const accuracy_tiebreak = accuracy_fit !== null ? 0.02 * accuracy_fit : 0.0;
  const ranking_score = Math.max(0.0, Math.min(100.0, base_score * prior_factor + accuracy_tiebreak));
  const center_prior: Record<string, any> =
    prior_factor < 1.0
      ? {
          enabled: true,
          factor: pythonRound(prior_factor, 4),
          certainty: birth_info["time_certainty"],
          entered_time: birth_info["entered_time"],
        }
      : { enabled: false };
  const moon_planet = natal_chart.planets["moon"];
  return {
    // Date-qualified key ("YYYY-MM-DDTHH:MM:SS") on a multi-day scan so same-clock-time candidates
    // on different days don't collide; plain "HH:MM:SS" otherwise (single-day / overnight).
    time: make_candidate_key(candidate, candidate_date),
    ascendant_sign: natal_chart.planets["ascendant"].sign_name,
    moon_sign: moon_planet !== null && moon_planet !== undefined ? Math.trunc(Number(moon_planet.sign)) : null,
    deterministic_score: pythonRound(ranking_score, 3),
    // Two-stage segments for display: EventFit (the time authority) and RisingSignFit.
    event_fit_score: event_fit !== null ? pythonRound(event_fit, 3) : null,
    // Per-time leave-one-out stability (worst single-event swing) — drives the drill-down Accuracy bar.
    accuracy_fit: accuracy_fit !== null ? pythonRound(accuracy_fit, 3) : null,
    // "Explained events" at THIS moment (weighted % of events scoring >50 + plain counts) — the
    // same Explains signal as Level 0, so the drill-down and Compare read one definition.
    ...(() => {
      const explained = explained_events_at({ kp_details });
      return {
        explained_fit: explained ? explained.fraction : null,
        explained_count: explained ? explained.explained_count : null,
        events_total: explained ? explained.events_total : null,
      };
    })(),
    rising_sign_fit_score: rising_sign_fit !== null ? pythonRound(rising_sign_fit, 3) : null,
    event_score: pythonRound(score, 3),
    kp_score: kp_score !== null ? pythonRound(kp_score, 3) : null,
    trait_score: trait_score !== null ? pythonRound(trait_score, 3) : null,
    trait_details: trait_details,
    life_period_score: life_period_score !== null ? pythonRound(life_period_score, 3) : null,
    life_period_details: life_period_details,
    ai_score: null,
    final_score: pythonRound(ranking_score, 3),
    assessment: "",
    event_details: details,
    kp_details: kp_details,
    event_scores: event_scores,
    special_points: special_points.available ? special_points.detail : null,
    // Weighted-evidence breakdown (events primary; appearance bounded) + the contradiction note,
    // so the report can show appearance as SECONDARY evidence, never the reason to reject a time.
    evidence_breakdown: {
      weights: evidence.used_weights,
      contributions: Object.fromEntries(
        Object.entries(evidence.contributions).map(([k, v]) => [k, pythonRound(v, 3)]),
      ),
      present: evidence.present,
      missing: evidence.missing,
    },
    appearance_note: appearance_conflict,
    center_time_prior: _truthy(center_prior["enabled"]) ? center_prior : null,
    generation_method: DETERMINISTIC_GENERATION_METHOD,
    search_pass: search_pass,
    coarse_seed_time: coarse_seed_time,
  };
}

export function candidate_time_window_bounds(record_or_time: Record<string, any> | string): [string, string] {
  let value: string;
  if (typeof record_or_time === "object" && record_or_time !== null) {
    value = String(
      _truthy(record_or_time["time_window"])
        ? record_or_time["time_window"]
        : _truthy(record_or_time["display_time"])
          ? record_or_time["display_time"]
          : _truthy(record_or_time["time"])
            ? record_or_time["time"]
            : "",
    );
  } else {
    value = String(record_or_time);
  }
  if (value.includes(" - ")) {
    const idx = value.indexOf(" - ");
    const start = value.slice(0, idx);
    const end = value.slice(idx + 3);
    return [start.trim(), end.trim()];
  }
  const parsed = time_label(time_label_to_tuple(value.trim()));
  return [parsed, parsed];
}

export function time_window_duration_seconds(start_time: string, end_time: string): number {
  const start_seconds = candidate_seconds(time_label_to_tuple(start_time));
  let end_seconds = candidate_seconds(time_label_to_tuple(end_time));
  if (end_seconds < start_seconds) {
    end_seconds += 86400;
  }
  return Math.max(1, end_seconds - start_seconds);
}

export function expanded_time_window_bounds(
  start_time: string,
  end_time: string,
  expansion_seconds: number,
): [string, string] {
  const start_seconds = candidate_seconds(time_label_to_tuple(start_time));
  const end_seconds = candidate_seconds(time_label_to_tuple(end_time));
  const expansion = Math.max(1, Math.trunc(expansion_seconds));
  const expanded_start = Math.max(0, start_seconds - expansion);
  const expanded_end = Math.min(86399, end_seconds + expansion);
  return [time_label(seconds_to_candidate(expanded_start)), time_label(seconds_to_candidate(expanded_end))];
}

export function child_time_window_seconds(parent_window_seconds: number): number {
  return Math.max(1, Math.floor(Math.trunc(parent_window_seconds) / 2));
}

export function time_window_for_record(
  record: Record<string, any>,
  pool: Array<Record<string, any>>,
  score_delta = 1.0,
  max_window_seconds: number | null = null,
): Record<string, any> {
  const selected_seconds = candidate_seconds(time_label_to_tuple(record["time"]));
  const selected_score = Number(record["deterministic_score"] ?? 0.0);
  if (max_window_seconds !== null && Math.trunc(max_window_seconds) <= 1) {
    return { display_time: record["time"], time_window: record["time"], precision_type: "exact_second" };
  }
  let nearby = pool
    .filter(
      (item) =>
        Math.abs(candidate_seconds(time_label_to_tuple(item["time"])) - selected_seconds) <= POLISH_RADIUS_SECONDS &&
        Number(item["deterministic_score"] ?? 0.0) >= selected_score - score_delta,
    )
    .sort((a, b) => candidate_seconds(time_label_to_tuple(a["time"])) - candidate_seconds(time_label_to_tuple(b["time"])));
  if (max_window_seconds !== null) {
    const span = Math.max(1, Math.trunc(max_window_seconds));
    const lower = Math.max(0, selected_seconds - Math.floor(span / 2));
    const upper = Math.min(86399, lower + span);
    nearby = nearby.filter((item) => {
      const s = candidate_seconds(time_label_to_tuple(item["time"]));
      return lower <= s && s <= upper;
    });
  }
  if (!nearby.length) {
    return { display_time: record["time"], time_window: record["time"], precision_type: "exact_second" };
  }
  const start = nearby[0]["time"];
  const end = nearby[nearby.length - 1]["time"];
  if (start === end) {
    return { display_time: record["time"], time_window: record["time"], precision_type: "exact_second" };
  }
  return {
    display_time: `${start} - ${end}`,
    time_window: `${start} - ${end}`,
    precision_type: "score_window",
    window_best_time: record["time"],
    window_score_delta: score_delta,
  };
}

/**
 * The scan step actually used to build `scored_by_sec`: the minimum positive gap between its
 * sorted keys (1 when there are fewer than two keys). Lets the plateau walk / entered-time
 * matching work on whatever grid the pass scanned (1s drill-down, 15s sign window, 60s coarse).
 */
const _scan_step_cache = new WeakMap<Map<number, Record<string, any>>, number>();

export function _scan_step_from_keys(scored_by_sec: Map<number, Record<string, any>>): number {
  // Memoised per map: the selection layer calls this once per SELECTED candidate over the same
  // (large) scored map, and the O(n log n) key sort only needs to happen once.
  const cached = _scan_step_cache.get(scored_by_sec);
  if (cached !== undefined) {
    return cached;
  }
  const keys = [...scored_by_sec.keys()].sort((a, b) => a - b);
  let step = Infinity;
  for (let i = 1; i < keys.length; i++) {
    const gap = keys[i] - keys[i - 1];
    if (gap > 0 && gap < step) {
      step = gap;
    }
  }
  const result = Number.isFinite(step) ? step : 1;
  _scan_step_cache.set(scored_by_sec, result);
  return result;
}

/**
 * Honest time window: the contiguous run of scanned seconds whose score stays within
 * `score_delta` of this record. The birth time cannot be resolved finer than the
 * span over which the score is flat, so the window reports that span rather than
 * fabricating an exact second off a plateau.
 *
 * The walk runs over the scan's OWN sampled keys (sorted adjacency, not fixed ±1s hops), so a
 * coarse pass (60s/15s) — or a merged multi-grid refinement pass — reports its real plateau
 * instead of finding no ±1s neighbours and over-claiming "exact_second" for every candidate.
 * Plain (time-of-day) key arrays are treated as circular so a plateau straddling midnight is
 * walked correctly; dated (multi-day) keys are absolute and keep their calendar day in the
 * reported window. A gap larger than 4× the scan step is an unscanned hole (a window boundary),
 * not plateau continuity. "exact_second" is only ever claimed when the immediate neighbours sit
 * 1s away — on a coarser grid an isolated peak is reported as its local grid cell, because that
 * is all the scan actually resolved. A scan that is flat end-to-end honestly reports the whole
 * scanned range.
 */
export function contiguous_plateau_window(
  record: Record<string, any>,
  scored_by_sec: Map<number, Record<string, any>>,
  score_delta = 1.0,
  max_window_seconds: number | null = null,
): Record<string, any> {
  const base = Number(record["deterministic_score"] ?? 0.0);
  // ABSOLUTE seconds (dated-key aware) so the plateau scan matches `scored_by_sec`'s keys.
  const center = candidate_key_seconds(record["time"]);
  const step = _scan_step_from_keys(scored_by_sec);
  // Dated (multi-day) keys are absolute/monotonic — no wrap; plain keys live on the 24h circle.
  const dated = is_dated_key(record["time"]);
  const keys = [...scored_by_sec.keys()].sort((a, b) => a - b);
  const n = keys.length;
  const center_idx = keys.indexOf(center);
  const radius_cap = max_window_seconds === null ? null : Math.max(0, Math.floor(Math.trunc(max_window_seconds) / 2));
  // Label an absolute key second — date-qualified for dated keys so multi-day windows never
  // drop the calendar day (two candidates at the same clock time on different days must not
  // read as the same window).
  const label_at = (second: number): string => {
    const t = time_label(seconds_to_candidate(second));
    return dated ? `${formatISODate(fromOrdinal(Math.floor(second / 86400)))}T${t}` : t;
  };
  if (center_idx < 0 || n <= 1) {
    return { display_time: record["time"], time_window: record["time"], precision_type: "exact_second" };
  }
  const gap_cap = step * 4; // adjacent samples of a (possibly merged) grid vs an unscanned hole
  // Gap from keys[i] to its previous sorted neighbour; plain keys wrap the 24h circle at i = 0.
  const gap_before = (i: number): number => {
    if (i > 0) return keys[i] - keys[i - 1];
    return dated ? Infinity : keys[0] + 86400 - keys[n - 1];
  };
  const idx_before = (i: number): number => (i > 0 ? i - 1 : n - 1);
  const within = (i: number): boolean =>
    Number(scored_by_sec.get(keys[i])!["deterministic_score"] ?? 0.0) >= base - score_delta;
  // Walk left/right over sorted-key adjacency, tracking cumulative distance from the centre.
  let lo_idx = center_idx;
  let lo_off = 0;
  let hi_idx = center_idx;
  let hi_off = 0;
  let extensions = 0;
  while (extensions < n - 1) {
    const gap = gap_before(lo_idx);
    const next = idx_before(lo_idx);
    if (gap > gap_cap || (radius_cap !== null && lo_off + gap > radius_cap) || !within(next)) break;
    lo_idx = next;
    lo_off += gap;
    extensions += 1;
  }
  while (extensions < n - 1) {
    const gap = gap_before(hi_idx === n - 1 ? 0 : hi_idx + 1);
    const next = hi_idx === n - 1 ? 0 : hi_idx + 1;
    if (gap > gap_cap || (radius_cap !== null && hi_off + gap > radius_cap) || !within(next)) break;
    hi_idx = next;
    hi_off += gap;
    extensions += 1;
  }
  if (extensions >= n - 1) {
    // The WHOLE scan is one flat plateau (no dated events / constant score): report the full
    // scanned range instead of letting a circular walk lap back onto the centre and fabricate
    // an isolated-peak precision claim.
    const start = label_at(keys[0]);
    const end = label_at(keys[n - 1]);
    return {
      display_time: `${start} - ${end}`,
      time_window: `${start} - ${end}`,
      precision_type: "score_window",
      window_best_time: record["time"],
      window_score_delta: score_delta,
      ...(step > 1 ? { scan_step_seconds: step } : {}),
    };
  }
  if (lo_idx === center_idx && hi_idx === center_idx) {
    // An isolated peak: the scan only resolved it to its LOCAL grid cell (half the gap to each
    // actual neighbour, capped), never an exact second — unless the neighbours really are 1s away.
    const left_gap = gap_before(center_idx);
    const right_gap = gap_before(center_idx === n - 1 ? 0 : center_idx + 1);
    const local = Math.min(left_gap, right_gap);
    const half = Math.min(Math.floor(local / 2), radius_cap ?? Infinity);
    if (local <= 1 || half < 1 || !Number.isFinite(half)) {
      return { display_time: record["time"], time_window: record["time"], precision_type: "exact_second" };
    }
    const cell_start = label_at(center - Math.min(Number.isFinite(left_gap) ? Math.floor(left_gap / 2) : half, half));
    const cell_end = label_at(center + Math.min(Number.isFinite(right_gap) ? Math.floor(right_gap / 2) : half, half));
    return {
      display_time: `${cell_start} - ${cell_end}`,
      time_window: `${cell_start} - ${cell_end}`,
      precision_type: "score_window",
      window_best_time: record["time"],
      window_score_delta: score_delta,
      scan_step_seconds: step,
    };
  }
  const start = label_at(keys[lo_idx]);
  const end = label_at(keys[hi_idx]);
  return {
    display_time: `${start} - ${end}`,
    time_window: `${start} - ${end}`,
    precision_type: "score_window",
    window_best_time: record["time"],
    window_score_delta: score_delta,
    ...(step > 1 ? { scan_step_seconds: step } : {}),
  };
}

/**
 * Guarantee the user's entered/recorded time always appears among the candidates.
 * A correct birth time can score below a sharp dasha peak yet still be the answer,
 * so it must never be silently pruned by spacing — the user has to be able to see
 * and judge it. Injects the exact entered second (flagged) if it isn't already shown.
 */
export function ensure_entered_time_in_selection(
  selected: Array<Record<string, any>>,
  scored_by_sec: Map<number, Record<string, any>>,
  center_time: string | null | undefined,
): Array<Record<string, any>> {
  if (!_truthy(center_time)) {
    return selected;
  }
  let target_seconds: number;
  try {
    target_seconds = candidate_seconds(time_label_to_tuple(center_time!));
  } catch {
    return selected;
  }
  // The pass may have scanned a coarse grid (60s/15s), on which the entered second is usually
  // NOT a key — silently dropping it there hid the user's own time. Fall back to the NEAREST
  // scanned second within half a scan step (circular for plain time-of-day keys; dated multi-day
  // keys are absolute, so a plain entered clock never spuriously matches one).
  let record = scored_by_sec.get(target_seconds);
  if (record === undefined) {
    const step = _scan_step_from_keys(scored_by_sec);
    let best_key: number | null = null;
    let best_gap = Infinity;
    for (const key of scored_by_sec.keys()) {
      const gap = key < 86400 ? circular_seconds_delta(key, target_seconds) : Math.abs(key - target_seconds);
      if (gap < best_gap) {
        best_gap = gap;
        best_key = key;
      }
    }
    if (best_key !== null && best_gap <= step / 2) {
      record = scored_by_sec.get(best_key);
    }
  }
  if (record === undefined) {
    return selected;
  }
  if (selected.some((item) => item["time"] === record!["time"])) {
    return selected;
  }
  const injected = { ...record };
  injected["is_entered_time"] = true;
  if (injected["time"] !== center_time) {
    // Nearest-grid stand-in, not the literal entered second — keep the original so any
    // "your entered time" surface can say what was actually entered.
    injected["entered_time_approximated"] = true;
    injected["entered_time"] = center_time;
  }
  return [...selected, injected];
}

/**
 * Re-score the CURRENTLY shown candidate times against the current events,
 * traits and life-periods — in place. Fast (only the handful of displayed times, no full
 * rescan) and AI-free, so the Event/Sign columns update the instant the user saves an
 * answer. Existing AI-review text and flags are preserved. Returns how many were rescored;
 * 0 means there are no candidate times yet (run matching first).
 */
export function rescore_candidates_in_place(user_profile: Record<string, any>): number {
  const candidates = candidate_list_from_times(user_profile, true);
  if (!candidates.length) {
    return 0;
  }
  const people_key = user_profile["slug"];
  const birth_data = birth_data_for_scoring(user_profile);
  const events = user_profile["events"] ?? [];
  const life_events: LifeEvent[] = [];
  for (const event of events) {
    try {
      life_events.push(event_payload_to_life_event(event));
    } catch (exc) {
      if (exc instanceof TypeError) throw exc; // Python catches only (KeyError, ValueError)
      continue;
    }
  }
  const timezone_offset = _py_float(user_profile["birth"]["timezone_offset"]);
  const search = _truthy(user_profile["search"]) ? user_profile["search"] : {};
  const traits = combined_trait_answers(user_profile); // rejected traits ride along (gates)
  const periods = _truthy(user_profile["life_periods"]) ? user_profile["life_periods"] : {};
  const appearance_likelihood = _truthy(user_profile["rising_sign_likelihood"])
    ? (user_profile["rising_sign_likelihood"] as Record<string, number>)
    : null;
  const preserve = [
    "ai_summary", "personality_paragraph", "milestone_periods", "physical_appearance",
    "event_fact_match", "other_notable_things", "evidence_for", "evidence_against",
    "assessment", "ai_score", "score_reason", "rejected", "is_entered_time", "chart_analysis",
  ];
  const updated: Array<Record<string, any>> = [];
  for (const candidate of candidates) {
    let tup: CandidateTuple;
    let cand_date: PlainDate | null = null;
    try {
      if (!("time" in candidate)) throw new Error("KeyError: 'time'");
      // Handles both plain "HH:MM:SS" and date-qualified multi-day keys.
      const parsed = parse_candidate_key(candidate["time"] as string);
      tup = parsed.tuple;
      cand_date = parsed.date;
    } catch {
      updated.push(candidate);
      continue;
    }
    const record = score_candidate_record(
      people_key, tup, birth_data, life_events, events, timezone_offset, {}, "rescore",
      null, search, traits, periods, appearance_likelihood, cand_date,
    );
    for (const key of preserve) {
      if (_not_empty(candidate[key])) {
        record[key] = candidate[key];
      }
    }
    updated.push(record);
  }
  updated.sort((a, b) => {
    const sa = a["deterministic_score"] !== null && a["deterministic_score"] !== undefined ? a["deterministic_score"] : -1.0;
    const sb = b["deterministic_score"] !== null && b["deterministic_score"] !== undefined ? b["deterministic_score"] : -1.0;
    return sb - sa;
  });
  store_candidate_times(user_profile, updated);
  return updated.length;
}

export function deterministic_top_candidates(
  user_profile: Record<string, any>,
  events: Array<Record<string, any>>,
  limit = 50,
  verbose = false,
): Array<Record<string, any>> {
  const previous_candidates = candidate_list_from_times(user_profile);
  // Traits (rising sign) and life-period themes (dasha) are chart-derived signals that
  // score even with no precise events — the whole point of the no-known-time case.
  const has_traits = _truthy(user_profile["traits"]) || _truthy(user_profile["rejected_traits"]);
  const has_periods = _truthy(user_profile["life_periods"]);
  if (!events.length && !has_traits && !has_periods) {
    if (verbose) {
      append_verbose_log(user_profile, "astro.matching.skipped", {
        reason:
          "Nothing to score. Add dated events, or answer the appearance/temperament or life-period questions to narrow the rising time.",
        candidate_times_count: candidate_list_from_times(user_profile, true).length,
      });
    }
    return [];
  }

  const people_key = user_profile["slug"];
  const birth_data = birth_data_for_scoring(user_profile);
  const life_events: LifeEvent[] = [];
  const skipped_events: Array<Record<string, any>> = [];
  for (const event of events) {
    try {
      life_events.push(event_payload_to_life_event(event));
    } catch (exc: any) {
      if (exc instanceof TypeError) throw exc; // Python catches only (KeyError, ValueError)
      skipped_events.push({ event: event, error: `${exc?.constructor?.name ?? "Error"}: ${exc?.message ?? exc}` });
      continue;
    }
  }
  if (!life_events.length && !has_traits && !has_periods) {
    if (verbose) {
      append_verbose_log(user_profile, "astro.matching.skipped", {
        reason:
          "Events were present, but none could be converted into LifeEvent scoring objects, and no traits/life-period answers were given.",
        raw_events: events,
        skipped_events: skipped_events,
      });
    }
    return [];
  }
  const scored: Array<Record<string, any>> = [];
  let search = user_profile["search"];
  const center_time = search["center_time"];
  // The prior's spread must match the stated uncertainty: a wide "approximate"
  // window should pull only gently toward the entered time, so chart evidence can
  // still move the answer hours away. Sigma = a quarter of the searched span.
  const prior_sigma_hours = Math.max(0.5, search_span_seconds(search) / 3600.0 / 4.0);
  const range_metadata = {
    center_time: center_time,
    center_source: "user_input",
    distance_penalty: _truthy(center_time) ? 15 : null,
    window_hours_each_side: _truthy(center_time) ? prior_sigma_hours : null,
  };

  const timezone_offset = _py_float(user_profile["birth"]["timezone_offset"]);
  search = user_profile["search"];
  if (verbose) {
    append_verbose_log(user_profile, "astro.matching.start", {
      note:
        "Candidate times are ranked by the deterministic chart engine: the KP signed-significator engine (event-date dasha + cuspal sub-lords) and the chart-derived trait engine (rising sign). No AI signal enters the ranking.",
      generation_method: DETERMINISTIC_GENERATION_METHOD,
      birth_data: birth_data,
      search: search,
      loop_count: user_profile["loop_count"] ?? 0,
      limit: limit,
      final_resolution_seconds: Math.trunc(
        Number(_truthy(search["final_resolution_seconds"]) ? search["final_resolution_seconds"] : POLISH_STEP_SECONDS),
      ),
      minimum_spacing_seconds_after_scoring: adaptive_min_gap_seconds(user_profile),
      refinement_window_radius_seconds: refinement_window_radius_seconds(user_profile),
      refinement_window_step_seconds: refinement_window_step_seconds(user_profile),
      raw_events: events,
      converted_life_events: life_events.map((event) => ({
        label: event.label,
        date: event.date,
        category: event.category,
        polarity: event.polarity,
        severity: event.severity,
        confidence: event.confidence,
        weight: event.weight,
        date_accuracy_days: event.date_accuracy_days,
        has_exact_time: event.has_exact_time,
        expected_ranges: event.expected_ranges,
        raw_values: event.raw_values,
        metric_weights: event.metric_weights,
        dasha_timing_weight: event.dasha_timing_weight,
        dasha_timing_window_days: event.dasha_timing_window_days,
      })),
      skipped_events: skipped_events,
    });
  }

  const trait_answers = combined_trait_answers(user_profile); // rejected traits ride along (gates)
  const life_period_answers = _truthy(user_profile["life_periods"]) ? user_profile["life_periods"] : {};
  const appearance_likelihood = _truthy(user_profile["rising_sign_likelihood"])
    ? (user_profile["rising_sign_likelihood"] as Record<string, number>)
    : null;
  for (const { tuple, date } of selected_scan_candidates(user_profile)) {
    scored.push(
      score_candidate_record(
        people_key,
        tuple,
        birth_data,
        life_events,
        events,
        timezone_offset,
        range_metadata,
        "coarse_chart_scan",
        null,
        search,
        trait_answers,
        life_period_answers,
        appearance_likelihood,
        date,
      ),
    );
  }
  if (verbose) {
    append_verbose_log(user_profile, "astro.matching.per_second_scores", {
      note:
        "Candidates were scored at the adaptive scan resolution for this window " +
        `(coarse ${FIRST_PASS_COARSE_STEP_SECONDS}s grid over a wide range as a seed-finder; ` +
        `${SIGN_WINDOW_SCAN_STEP_SECONDS}s once narrowed to a single rising sign; every second ` +
        "only on an explicit drill-down). The exact second is reached by drilling down on a " +
        "candidate, not by this pass.",
      candidate_count: scored.length,
      scores: scored,
    });
  }

  const min_gap_seconds = adaptive_min_gap_seconds(user_profile);
  // `scored` holds every candidate at this window's adaptive scan resolution (coarse on a
  // wide range, fine once narrowed, every second only on an explicit drill-down). We do not
  // refine further here; we only bunch the output by selecting spaced top-scoring candidates.
  // The exact second is reached by drilling down on a candidate, which rescans at 1s.
  const final_candidates = scored;
  let selected: Array<Record<string, any>>;
  let selection_note: string;
  if (Math.trunc(Number(user_profile["loop_count"] ?? 0)) > 0 && previous_candidates.length) {
    selected = select_top_per_previous_center(
      final_candidates,
      previous_candidates,
      REFINEMENT_RESULTS_PER_PRIOR_TIME,
      min_gap_seconds,
    ).slice(0, limit);
    selection_note = "top ranked exact-second candidates with a maximum of three results per previous candidate time";
  } else {
    selected = select_spaced_candidates_seconds(final_candidates, limit, min_gap_seconds);
    selection_note = "top ranked exact-second candidates selected with the adaptive minimum spacing for this loop";
  }
  if (verbose) {
    append_verbose_log(user_profile, "astro.matching.final_selected", {
      generation_method: DETERMINISTIC_GENERATION_METHOD,
      selection: selection_note,
      minimum_spacing_seconds: min_gap_seconds,
      maximum_results_per_previous_time:
        Math.trunc(Number(user_profile["loop_count"] ?? 0)) > 0 ? REFINEMENT_RESULTS_PER_PRIOR_TIME : null,
      final_resolution_seconds: POLISH_STEP_SECONDS,
      saved_candidate_count: selected.length,
      saved_candidates: selected,
    });
  }
  const max_window_seconds_raw = search["max_time_window_seconds"];
  let max_window_seconds: number | null;
  try {
    if (max_window_seconds_raw === null || max_window_seconds_raw === undefined) {
      max_window_seconds = null;
    } else {
      // Python int() — raises on garbage (caught), truncates floats only via int(float), so
      // a non-integer string raises ValueError → null.
      const text = String(max_window_seconds_raw).trim();
      if (typeof max_window_seconds_raw === "number") {
        max_window_seconds = Math.trunc(max_window_seconds_raw);
      } else if (/^[+-]?\d+$/.test(text)) {
        max_window_seconds = Number(text);
      } else {
        throw new Error(`invalid literal for int() with base 10: '${max_window_seconds_raw}'`);
      }
    }
  } catch {
    max_window_seconds = null;
  }

  // Always surface the user's entered/recorded time so a correct-but-not-top time
  // is never hidden by spacing, then report each candidate's honest plateau window.
  const scored_by_sec = new Map<number, Record<string, any>>();
  for (const item of final_candidates) {
    // Keyed by the candidate's ABSOLUTE seconds (dated-key aware) so multi-day candidates at the
    // same clock time on different days don't collide; identical to seconds-of-day for plain keys.
    scored_by_sec.set(candidate_key_seconds(item["time"]), item);
  }
  selected = ensure_entered_time_in_selection(selected, scored_by_sec, center_time);
  selected = [...selected].sort(
    (a, b) => Number(b["deterministic_score"] ?? 0.0) - Number(a["deterministic_score"] ?? 0.0),
  );
  for (const item of selected) {
    Object.assign(item, contiguous_plateau_window(item, scored_by_sec, 1.0, max_window_seconds));
  }
  return selected;
}

/**
 * Ranking is deterministic-only. The AI score is retained on candidates for display
 * but never enters the ranking, so an AI hallucination cannot move the answer. (This
 * deliberately ignores ai_score; AI_WEIGHT/DETERMINISTIC_WEIGHT are no longer applied.)
 */
export function combine_scores(deterministic_score: number | null, ai_score: number | null): number | null {
  if (deterministic_score !== null && deterministic_score !== undefined) {
    return pythonRound(Number(deterministic_score), 3);
  }
  if (ai_score !== null && ai_score !== undefined) {
    return pythonRound(Number(ai_score), 3);
  }
  return null;
}

/**
 * Attach the AI's qualitative assessment (and its non-binding ai_score) to candidates
 * for DISPLAY only. final_score stays equal to the deterministic score and the order is
 * never changed by the AI.
 */
export function apply_ai_assessment(
  candidates: Array<Record<string, any>>,
  ai_response: Record<string, any>,
): Array<Record<string, any>> {
  const by_time = new Map<any, Record<string, any>>();
  for (const item of ai_response["candidate_scores"] ?? []) {
    by_time.set(item["time"], item);
  }
  const updated: Array<Record<string, any>> = [];
  for (const candidate of candidates) {
    const ai_item = by_time.get(candidate["time"]) ?? {};
    const merged = { ...candidate };
    if ("ai_score" in ai_item && ai_item["ai_score"] !== null && ai_item["ai_score"] !== undefined) {
      merged["ai_score"] = pythonRound(Number(ai_item["ai_score"]), 3); // shown, not ranked
    }
    merged["assessment"] = ai_item["assessment"] ?? merged["assessment"] ?? "";
    merged["final_score"] = combine_scores(merged["deterministic_score"] ?? null, merged["ai_score"] ?? null);
    updated.push(merged);
  }
  // Sort strictly by the deterministic score; the AI never reorders candidates.
  return updated.sort((a, b) => {
    const sa = a["deterministic_score"] !== null && a["deterministic_score"] !== undefined ? a["deterministic_score"] : -1;
    const sb = b["deterministic_score"] !== null && b["deterministic_score"] !== undefined ? b["deterministic_score"] : -1;
    return sb - sa;
  });
}
